import React, { useState, useEffect } from 'react';
import { FileCheck, CheckCircle, XCircle, MessageSquare, Loader2, AlertTriangle } from 'lucide-react';
import { API } from '../../App';

function ReviewQueue({ wsId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('in_review');
  const [actionLoading, setActionLoading] = useState('');
  const [comment, setComment] = useState('');
  const [commenting, setCommenting] = useState('');

  const load = () => {
    if (!wsId) return;
    setLoading(true);
    API.get(`/api/workspaces/${wsId}/content/review-queue?status=${filter}`)
      .then(r => { setItems(r.data.submissions || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [wsId, filter]);

  const review = async (id, action) => {
    setActionLoading(id);
    try {
      await API.post(`/api/workspaces/${wsId}/content/review/${id}`, { action, comment: comment || '' });
      setCommenting('');
      setComment('');
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    }
    setActionLoading('');
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading queue...</div>;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {['in_review', 'approved', 'rejected', 'changes_requested'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs font-mono min-h-[44px] ${filter === s ? 'bg-[#FF4D00] text-black' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0]'}`}>
            {s.replace(/_/g, ' ')} {filter === s && total > 0 ? `(${total})` : ''}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-[#A0A0A0]">
          <FileCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No items with status "{filter.replace(/_/g, ' ')}"</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className="p-3 bg-[#0F0F0F] border border-[#2A2A2A] space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="text-xs font-mono text-[#FF4D00]">{item.content_type}</span>
                  <p className="text-sm">{item.submitter_name || item.submitter_email}</p>
                  <p className="text-xs text-[#A0A0A0]">{new Date(item.submitted_at).toLocaleString()}</p>
                </div>
                {filter === 'in_review' && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => review(item.id, 'approve')} disabled={actionLoading === item.id}
                      className="p-2 text-[#22C55E] hover:bg-[#22C55E]/10 min-w-[44px] min-h-[44px] flex items-center justify-center">
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button onClick={() => setCommenting(commenting === item.id ? '' : item.id)}
                      className="p-2 text-[#FF4D00] hover:bg-[#FF4D00]/10 min-w-[44px] min-h-[44px] flex items-center justify-center">
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button onClick={() => review(item.id, 'reject')} disabled={actionLoading === item.id}
                      className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 min-w-[44px] min-h-[44px] flex items-center justify-center">
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              {commenting === item.id && (
                <div className="flex gap-2">
                  <input value={comment} onChange={e => setComment(e.target.value)}
                    placeholder="Comment..." className="flex-1 px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" />
                  <button onClick={() => review(item.id, 'request_changes')}
                    className="px-3 py-2 bg-[#FF4D00] text-black text-xs font-medium min-h-[44px]">Request Changes</button>
                </div>
              )}
              {item.review_comment && (
                <p className="text-xs text-[#A0A0A0] italic border-l-2 border-[#2A2A2A] pl-2">{item.review_comment}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RevocationPanel({ wsId }) {
  const [contentType, setContentType] = useState('persona');
  const [contentId, setContentId] = useState('');
  const [revokeType, setRevokeType] = useState('security');
  const [reason, setReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [result, setResult] = useState(null);

  const revoke = async () => {
    if (!contentId.trim()) return;
    if (!window.confirm(`EMERGENCY REVOCATION: This will immediately ${revokeType === 'security' ? 'revoke' : 'schedule revocation of'} the content. Continue?`)) return;
    setRevoking(true);
    setResult(null);
    try {
      const r = await API.post(`/api/workspaces/${wsId}/content/revoke`, {
        content_type: contentType, content_id: contentId.trim(),
        revocation_type: revokeType, reason,
      });
      setResult(r.data);
      setContentId('');
      setReason('');
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Revocation failed' });
    }
    setRevoking(false);
  };

  return (
    <div className="mt-6 p-4 border border-[#EF4444]/30 bg-[#EF4444]/5">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
        <h3 className="text-sm font-bold text-[#EF4444]">Emergency Content Revocation</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <select value={contentType} onChange={e => setContentType(e.target.value)}
          className="px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]">
          <option value="persona">Persona</option>
          <option value="playbook">Playbook</option>
        </select>
        <select value={revokeType} onChange={e => setRevokeType(e.target.value)}
          className="px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]">
          <option value="security">Security (immediate)</option>
          <option value="policy">Policy (72h grace)</option>
        </select>
      </div>
      <input value={contentId} onChange={e => setContentId(e.target.value)}
        placeholder="Content ID" className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm mb-2 font-mono focus:outline-none focus:border-[#FF4D00]" />
      <input value={reason} onChange={e => setReason(e.target.value)}
        placeholder="Reason for revocation" className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-sm mb-2 focus:outline-none focus:border-[#FF4D00]" />
      <button onClick={revoke} disabled={revoking || !contentId.trim()}
        className="px-4 py-2 bg-[#EF4444] text-white text-sm font-medium disabled:opacity-50 min-h-[44px]">
        {revoking ? 'Revoking...' : 'Revoke Content'}
      </button>
      {result && (
        <div className={`mt-2 p-2 text-xs font-mono ${result.error ? 'text-[#EF4444]' : 'text-[#22C55E]'}`}>
          {result.error || `Revoked. ${result.grace_period_ends ? `Grace period ends: ${new Date(result.grace_period_ends).toLocaleString()}` : 'Immediate effect.'}`}
        </div>
      )}
    </div>
  );
}

export default function ContentReviewSection({ wsId }) {
  return (
    <div>
      <ReviewQueue wsId={wsId} />
      <RevocationPanel wsId={wsId} />
    </div>
  );
}
